/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Table from "components/Table";
import ContentShell from "components/ContentShell";
import {
  Box,
  IconButton,
  MenuItem,
  Menu,
  Switch,
  FormControlLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchInput from "components/SearchInput";
import { SimpleSelectInput } from "components/FormSelectInput";
import { makeStyles } from "@mui/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "hooks/useSnackbar";
import configData from "config/config.json";
import { operatorTableColumns } from "utils/tableHelper/pobTableHelper";
import {
  deleteOperator,
  getOperators,
  updateOperatorStatus,
  updateOperatorLiveStatus,
  toggleGlobalMaintenance,
  permanentDeleteOperator,
  updateOperatorFilterPreference,
  resetOperatorFilterPreference,
} from "services/operatorService";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import { defaultPageNo, defaultPageSize } from "utils/constants";
import { sortArray } from "utils/miscUtils";
import useConsent from "hooks/useConsent";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import GlobalMaintenanceConfirmDialog from "./components/GlobalMaintenanceConfirmDialog";
import sessionHelperInstance from "services/sessionHelper";
import {
  LIVE_STATUS_OPTIONS,
  ACTIVE_STATUS_OPTIONS,
} from "utils/operatorFilterOptions";
import QuickOperatorAddModal from "./components/QuickOperatorAddModal";

const useStyles = makeStyles((theme: any) => ({
  playerCardParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
}));

interface RowActionsProps {
  rowId?: any;
  data: any;
  actionHandlers: {
    handleDelete?: (data: any) => void;
    handleDisable?: (id: any) => void;
    handlePermanentDelete?: (id: any) => void;
    handleToggleLiveStatus?: (id: any) => void;
  };
}

const RowActions = ({ rowId, data, actionHandlers }: RowActionsProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const {
    handleDelete = () => {},
    handleDisable = () => {},
    handlePermanentDelete = () => {},
    handleToggleLiveStatus = () => {},
  } = actionHandlers;

  const handleClose = (event?: any) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleEditClick = (event: any, data: any) => {
    handleClose(event);
    navigate(configData.routes.operators.edit?.replace(":id", data?._id));
  };

  const handleDisableClick = (event: any, id: any) => {
    handleClose(event);
    handleDisable(id);
  };

  const handleToggleLiveStatusClick = (event: any, id: any) => {
    handleClose(event);
    handleToggleLiveStatus(id);
  };

  const handleDeleteClick = (event: any, rowData: any) => {
    handleClose(event);
    handleDelete(rowData);
  };

  const handlePermanentDeleteClick = (event: any, rowData: any) => {
    handleClose(event);
    handlePermanentDelete(rowData);
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.OPERATOR.DELETE_ROLES,
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={(e) => handleEditClick(e, data)}>Edit</MenuItem>
        <MenuItem onClick={(e) => handleDisableClick(e, data?._id)}>
          {data.isActive ? "Disable" : "Enable"}
        </MenuItem>
        <MenuItem onClick={(e) => handleToggleLiveStatusClick(e, data?._id)}>
          {data?.isLive ? "Mark as Test" : "Mark as Live"}
        </MenuItem>
        {hasDeletePermission && (
          <>
            <MenuItem onClick={(e) => handleDeleteClick(e, data)}>
              Delete
            </MenuItem>
            {data?.isLive === false && (
              <MenuItem onClick={(e) => handlePermanentDeleteClick(e, data)}>
                Permanent Delete
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </>
  );
};

const Operators = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  // Single source of truth for URL params - shared across the component
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [orderDirection, setOrderDirection] = useState("asc");

  // Global maintenance state
  const [globalMaintenanceEnabled, setGlobalMaintenanceEnabled] =
    useState(false);
  const [globalMaintenanceLoading, setGlobalMaintenanceLoading] =
    useState(false);
  const [globalMaintenanceDialogOpen, setGlobalMaintenanceDialogOpen] =
    useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  // Read search value from URL params directly
  const searchValue = searchParams.get("search") || "";

  const [liveStatusFilter, setLiveStatusFilter] = useState(
    () =>
      searchParams.get("liveStatus") ||
      sessionHelper.operatorFilterPreference?.liveStatus ||
      "live",
  );
  const [activeStatusFilter, setActiveStatusFilter] = useState(
    () =>
      searchParams.get("activeStatus") ||
      sessionHelper.operatorFilterPreference?.activeStatus ||
      "enabled",
  );
  const [filterPrefLoading, setFilterPrefLoading] = useState(false);

  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const {
    isOpenConsent: isOpenPermanentDeleteConsent,
    constentDetails: permanentDeleteConsentDetails,
    openConsent: openPermanentDeleteConsent,
    closeConsent: closePermanentDeleteConsent,
    updateDetails: updatePermanentDeleteDetails,
  } = useConsent();

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.PLATFORM.UPDATE_ROLES,
  );

  // Read pagination values from URL params directly
  const pageNo = Number(searchParams.get("pageNo")) || defaultPageNo;
  const pageSize = Number(searchParams.get("pageSize")) || defaultPageSize;

  // Unified pagination handlers using single setSearchParams
  const onChangePage = (_pageNo: number) => {
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("pageNo", String(_pageNo));
      return updatedParams;
    });
  };

  const onPageSizeChange = (event: any) => {
    const newPageSize = Number(event?.target?.value ?? defaultPageSize);
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("pageNo", "1");
      updatedParams.set("pageSize", String(newPageSize));
      return updatedParams;
    });
  };

  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const fetchData = async (search?: any) => {
    setLoading(true);
    try {
      let payload = {
        pageNo,
        pageSize,
        liveStatus: liveStatusFilter,
        activeStatus: activeStatusFilter,
      };
      if (searchValue) payload["search"] = searchValue;
      const resp = await getOperators(payload);
      const operators = resp?.data?.operators || [];
      setFilteredList(
        operators.map((el) => ({
          ...el,
          coinPurchaseAllowedText: el?.coinPurchaseAllowed
            ? "Allowed"
            : "Not Allowed",
          isActiveText: el?.isActive ? "YES" : "NO",
          isLiveText: el?.isLive === false ? "Test" : "Live",
          isUnderMaintenance: el?.isUnderMaintenance ? "Yes" : "No",
          rowDisabled: !el?.isActive,
        })),
      );
      // Get global maintenance status from the response
      setGlobalMaintenanceEnabled(resp?.data?.globalEnabled ?? false);
      setLoading(false);
    } catch (error) {
      showSnackbar(error.message);
      setLoading(false);
    }
  };

  const updateOperatorDropdownOptions = async (filterOverride?: {
    liveStatus: string;
    activeStatus: string;
  }) => {
    let payload = {
      skipPagination: true,
      liveStatus: filterOverride?.liveStatus ?? liveStatusFilter,
      activeStatus: filterOverride?.activeStatus ?? activeStatusFilter,
    };
    const resp = await getOperators(payload);
    const operators = resp?.data?.operators || [];
    sessionHelperInstance.applyOperatorFilterToCaches(operators);
  };

  const handleGlobalMaintenanceToggleClick = () => {
    setGlobalMaintenanceDialogOpen(true);
  };

  const handleGlobalMaintenanceConfirm = async () => {
    setGlobalMaintenanceLoading(true);
    try {
      const newValue = !globalMaintenanceEnabled;
      const resp = await toggleGlobalMaintenance(newValue);
      setGlobalMaintenanceEnabled(resp?.data?.globalEnabled ?? newValue);
      showSnackbar(
        resp?.data?.message ||
          `Global maintenance ${newValue ? "enabled" : "disabled"}`,
      );
      setGlobalMaintenanceDialogOpen(false);
      // Refresh the operators list to reflect changes
      fetchData(searchValue);
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setGlobalMaintenanceLoading(false);
    }
  };

  const handleGlobalMaintenanceDialogClose = () => {
    if (!globalMaintenanceLoading) {
      setGlobalMaintenanceDialogOpen(false);
    }
  };

  const onSearch = (value: any) => {
    // Update both search and pageNo in URL params together atomically
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      if (value) {
        updatedParams.set("search", value);
      } else {
        updatedParams.delete("search");
      }
      updatedParams.set("pageNo", String(defaultPageNo));
      return updatedParams;
    });
  };

  const applyOperatorFilterChange = async (
    newLiveStatus: string,
    newActiveStatus: string,
  ) => {
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("liveStatus", newLiveStatus);
      updatedParams.set("activeStatus", newActiveStatus);
      updatedParams.set("pageNo", String(defaultPageNo));
      return updatedParams;
    });

    setFilterPrefLoading(true);
    try {
      const resp = await updateOperatorFilterPreference({
        liveStatus: newLiveStatus,
        activeStatus: newActiveStatus,
      });
      sessionHelperInstance.setOperatorFilterPreference(
        resp?.data?.operatorFilterPreference,
      );
      await updateOperatorDropdownOptions({
        liveStatus: newLiveStatus,
        activeStatus: newActiveStatus,
      });
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setFilterPrefLoading(false);
    }
  };

  const onLiveStatusFilterChange = (e: any) => {
    const newLiveStatus = e?.target?.value;
    setLiveStatusFilter(newLiveStatus);
    applyOperatorFilterChange(newLiveStatus, activeStatusFilter);
  };

  const onActiveStatusFilterChange = (e: any) => {
    const newActiveStatus = e?.target?.value;
    setActiveStatusFilter(newActiveStatus);
    applyOperatorFilterChange(liveStatusFilter, newActiveStatus);
  };

  const onOperatorFilterReset = async () => {
    setFilterPrefLoading(true);
    try {
      const resp = await resetOperatorFilterPreference();
      const preference = resp?.data?.operatorFilterPreference;
      sessionHelperInstance.setOperatorFilterPreference(preference);
      const resetLiveStatus = preference?.liveStatus ?? "live";
      const resetActiveStatus = preference?.activeStatus ?? "enabled";
      setLiveStatusFilter(resetLiveStatus);
      setActiveStatusFilter(resetActiveStatus);
      setSearchParams((prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);
        updatedParams.set("liveStatus", resetLiveStatus);
        updatedParams.set("activeStatus", resetActiveStatus);
        updatedParams.set("pageNo", String(defaultPageNo));
        return updatedParams;
      });
      await updateOperatorDropdownOptions({
        liveStatus: resetLiveStatus,
        activeStatus: resetActiveStatus,
      });
      showSnackbar("Operator filter preference reset");
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setFilterPrefLoading(false);
    }
  };

  const createOperator = () => navigate(configData.routes.operators.create);

  const onQuickAddCreated = (operator: any) => {
    setQuickAddOpen(false);
    showSnackbar("Operator created successfully");
    fetchData(searchValue);
    updateOperatorDropdownOptions();
    if (operator?._id) {
      navigate(configData.routes.operators.edit?.replace(":id", operator._id));
    }
  };

  const rowHref = (data: any) =>
    hasEditPermission
      ? configData.routes.operators.edit?.replace(":id", data?._id)
      : null;

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({ itemName: data.operatorName, id: data._id });
  };

  const _openPermanentDeleteConsentDialog = (data: any) => {
    openPermanentDeleteConsent();
    updatePermanentDeleteDetails({ itemName: data.operatorName, id: data._id });
  };

  const onDelete = async () => {
    try {
      const resp = await deleteOperator(constentDetails.id);
      showSnackbar(resp?.data?.message);
      fetchData(searchValue);
      updateOperatorDropdownOptions();
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

  const onPermanentDelete = async () => {
    setDeleteLoading(true);
    try {
      const resp = await permanentDeleteOperator(
        permanentDeleteConsentDetails.id,
      );
      showSnackbar(resp?.data?.message);
      fetchData(searchValue);
      updateOperatorDropdownOptions();
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      closePermanentDeleteConsent();
      setDeleteLoading(false);
    }
  };

  const onDisable = async (id) => {
    try {
      const resp = await updateOperatorStatus(id);
      showSnackbar(resp?.data?.message);
      fetchData(searchValue);
    } catch (error) {
      showSnackbar(error.message);
    }
  };

  const onToggleLiveStatus = async (id) => {
    try {
      const resp = await updateOperatorLiveStatus(id);
      showSnackbar(resp?.data?.message);
      fetchData(searchValue);
      updateOperatorDropdownOptions();
    } catch (error) {
      showSnackbar(error.message);
    }
  };

  useEffect(() => {
    const initialColumns = operatorTableColumns().filter(
      (col) => col.id !== "actions" || hasEditPermission,
    );
    setColumns(initialColumns);

    fetchData(searchValue);
  }, [pageNo, pageSize, searchValue, liveStatusFilter, activeStatusFilter]);

  return (
    <ContentShell
      title="Operators"
      loading={loading}
      actions={
        <Box
          style={{
            display: "flex",
            flexFlow: "row nowrap",
            gap: 8,
            alignItems: "center",
          }}
        >
          {/* Global Maintenance Toggle */}
          {hasEditPermission && (
            <Tooltip
              title={
                globalMaintenanceEnabled
                  ? "Global maintenance is currently enabled. Click to disable."
                  : "Enable global maintenance for all active operators."
              }
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: globalMaintenanceEnabled
                    ? "#ffebee"
                    : "#e8f5e9",
                  borderRadius: "8px",
                  padding: "4px 12px",
                  border: globalMaintenanceEnabled
                    ? "1px solid #f44336"
                    : "1px solid #4caf50",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    color: globalMaintenanceEnabled ? "#d32f2f" : "#2e7d32",
                    mr: 1,
                  }}
                >
                  Global Maintenance
                </Typography>
                <Switch
                  checked={globalMaintenanceEnabled}
                  onChange={handleGlobalMaintenanceToggleClick}
                  color={globalMaintenanceEnabled ? "error" : "success"}
                  size="small"
                />
              </Box>
            </Tooltip>
          )}

          <Tooltip
            title="Filter operators by Live/Test status"
            PopperProps={{ style: { zIndex: 1200 } }}
          >
            <Box sx={{ minWidth: 110 }}>
              <SimpleSelectInput
                name="liveStatusFilter"
                value={liveStatusFilter}
                options={LIVE_STATUS_OPTIONS}
                onChange={onLiveStatusFilterChange}
                disabled={filterPrefLoading}
                customStyle={{ height: 40 }}
              />
            </Box>
          </Tooltip>
          <Tooltip
            title="Filter operators by Enabled/Disabled status"
            PopperProps={{ style: { zIndex: 1200 } }}
          >
            <Box sx={{ minWidth: 130 }}>
              <SimpleSelectInput
                name="activeStatusFilter"
                value={activeStatusFilter}
                options={ACTIVE_STATUS_OPTIONS}
                onChange={onActiveStatusFilterChange}
                disabled={filterPrefLoading}
                customStyle={{ height: 40 }}
              />
            </Box>
          </Tooltip>
          <Tooltip title="Reset saved filter to Live - Enabled">
            <span>
              <CustomButton
                startIcon={<RestartAltIcon />}
                onClick={onOperatorFilterReset}
                disabled={filterPrefLoading}
              >
                Reset Filter
              </CustomButton>
            </span>
          </Tooltip>

          <SearchInput
            value={searchValue}
            placeholder="Search an operator"
            onSearch={onSearch}
          />
          {sessionHelper.hasRole(PermittedRoles.LEAGUE.CREATE_ROLES) && (
            <>
              <CustomButton onClick={createOperator}>Create</CustomButton>
              <CustomButton onClick={() => setQuickAddOpen(true)}>
                Quick Add
              </CustomButton>
            </>
          )}
        </Box>
      }
    >
      <Box className={styles.playerCardParent}>
        <Table
          columns={columns}
          data={filteredList}
          currentPage={pageNo}
          actions={RowActions}
          pageSize={pageSize}
          totalEntries={filteredList.length}
          onChangePage={onChangePage}
          onColumnClick={onColumnClick}
          onRowsPerPageChange={onPageSizeChange}
          rowHref={rowHref}
          highlightAltRow={true}
          actionHandlers={{
            handleDisable: onDisable,
            handleToggleLiveStatus: onToggleLiveStatus,
            handleDelete: _openConsentDialog,
            handlePermanentDelete: _openPermanentDeleteConsentDialog,
          }}
        />
        <DeleteConsentDialog
          itemName={constentDetails.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
        />

        <DeleteConsentDialog
          itemName={permanentDeleteConsentDetails.itemName}
          open={isOpenPermanentDeleteConsent}
          onClose={closePermanentDeleteConsent}
          onConfirm={onPermanentDelete}
          btnText="Permanent Delete"
          message="Are you sure you want to permanently delete "
          loading={deleteLoading}
        />

        <GlobalMaintenanceConfirmDialog
          open={globalMaintenanceDialogOpen}
          onClose={handleGlobalMaintenanceDialogClose}
          onConfirm={handleGlobalMaintenanceConfirm}
          isCurrentlyEnabled={globalMaintenanceEnabled}
          loading={globalMaintenanceLoading}
        />

        <QuickOperatorAddModal
          open={quickAddOpen}
          onClose={() => setQuickAddOpen(false)}
          onCreated={onQuickAddCreated}
        />
      </Box>
    </ContentShell>
  );
};

export default Operators;
